import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { StorefrontIcon as Storefront, CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, WarningIcon as Warning, CurrencyDollarIcon as CurrencyDollar, PackageIcon as Package, TShirtIcon as TShirt, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

/**
 * Admin-only platform health view for every active Dog Shop storefront.
 *
 * Surfaces the four gates that matter for a healthy seller:
 *   1. Active subscription
 *   2. Stripe Connect payouts enabled
 *   3. (optional) Printify connected, auto-fulfill on, recent sync
 *   4. Recent revenue + product count + any fulfillment errors
 *
 * Read-only — admins fix problems by impersonating the seller or
 * messaging them, not by editing here. Same posture as Stripe's own
 * Connect dashboard.
 */
export default function AdminStorefrontsHealth() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/storefronts/health")
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error(formatApiError(e));
        setData({ summary: { total: 0 }, stores: [] });
      });
  }, []);

  if (!data) return <AppShell><div className="max-w-6xl mx-auto px-4 py-10">Loading…</div></AppShell>;

  const { summary, stores } = data;
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—";

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-16" data-testid="admin-storefronts-health">
        <header className="mb-6">
          <Link to="/admin" className="text-[12px] text-[#6B6B6B] hover:text-ink mb-2 inline-block">
            ← Back to admin
          </Link>
          <h1 className="font-spectral text-2xl sm:text-3xl tracking-tight">Storefront health</h1>
          <p className="text-[13px] sm:text-sm text-[#6B6B6B] mt-1 max-w-2xl">
            One row per active Dog Shop storefront. Spot-check that subscriptions
            are paid, payouts are wired, and Printify is healthy. Click into a
            store to see its public page; use the seller&rsquo;s email if you
            need to reach out.
          </p>
        </header>

        {/* Summary tiles */}
        <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="admin-storefronts-health-summary">
          <Tile label="Active stores" value={summary.total} />
          <Tile label="Subscribed" value={summary.subscribed} good={summary.subscribed === summary.total} />
          <Tile label="Payouts ready" value={summary.payouts_ready} good={summary.payouts_ready === summary.total} />
          <Tile label="Printify on" value={summary.printify_connected} subtle />
          <Tile label="Auto-fulfill on" value={summary.auto_fulfill_on} subtle />
          <Tile label="Fulfillment errors" value={summary.fulfillment_errors} bad={summary.fulfillment_errors > 0} />
        </section>

        {/* Stores table — collapses to cards on mobile via tailwind */}
        <section className="rounded-2xl border border-[#EAEAEA] bg-white overflow-hidden" data-testid="admin-storefronts-health-table">
          {stores.length === 0 ? (
            <p className="text-[13px] text-[#6B6B6B] p-6 text-center">No active storefronts yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="bg-[#FAFAFA] text-[10.5px] uppercase tracking-wider text-[#6B6B6B]">
                  <tr>
                    <th className="text-left px-3 py-2">Store</th>
                    <th className="text-center px-2 py-2">Sub</th>
                    <th className="text-center px-2 py-2">Payouts</th>
                    <th className="text-center px-2 py-2">Printify</th>
                    <th className="text-right px-3 py-2">Products</th>
                    <th className="text-right px-3 py-2">30d revenue</th>
                    <th className="text-left px-3 py-2">Last order</th>
                    <th className="text-left px-3 py-2">Owner</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {stores.map((s) => (
                    <tr
                      key={s.id}
                      className="border-t border-[#F1ECDD]"
                      data-testid={`admin-storefront-row-${s.slug}`}
                    >
                      <td className="px-3 py-2.5">
                        <div className="font-bold text-ink truncate max-w-[180px]">{s.name}</div>
                        <Link to={`/store/${s.slug}`} target="_blank" rel="noreferrer" className="text-[10.5px] text-gold-dark hover:underline">/store/{s.slug}</Link>
                      </td>
                      <td className="text-center px-2 py-2.5">
                        {s.subscribed ? (
                          <span title={s.subscription_tier} className="inline-flex"><CheckCircle size={14} weight="fill" className="text-emerald-600" /></span>
                        ) : (
                          <XCircle size={14} weight="fill" className="text-crimson inline" />
                        )}
                      </td>
                      <td className="text-center px-2 py-2.5">
                        {s.payouts_ready ? (
                          <CheckCircle size={14} weight="fill" className="text-emerald-600 inline" />
                        ) : (
                          <span title={s.has_connect_account ? "Onboarding incomplete" : "No Connect account"} className="inline-flex">
                            <Warning size={14} weight="fill" className="text-amber-600" />
                          </span>
                        )}
                      </td>
                      <td className="text-center px-2 py-2.5">
                        {s.printify_connected ? (
                          <div className="flex flex-col items-center gap-0.5">
                            <TShirt size={14} weight="duotone" className="text-gold-dark" />
                            <span className="text-[9.5px] text-[#6B6B6B] truncate max-w-[80px]">
                              {s.printify_auto_fulfill ? "auto · " : ""}{fmtDate(s.printify_last_synced_at)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-[10px] text-[#9A9A9A]">—</span>
                        )}
                      </td>
                      <td className="text-right px-3 py-2.5 font-tabular">{s.product_count}</td>
                      <td className="text-right px-3 py-2.5 font-tabular">${(s.revenue_30d_cents / 100).toFixed(0)}</td>
                      <td className="px-3 py-2.5 text-[#6B6B6B]">
                        {s.last_order_at ? (
                          <>
                            {fmtDate(s.last_order_at)}
                            {s.fulfillment_errors_count > 0 && (
                              <div className="text-[10px] text-crimson mt-0.5">
                                {s.fulfillment_errors_count} fulfillment error{s.fulfillment_errors_count === 1 ? "" : "s"}
                              </div>
                            )}
                          </>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-[#6B6B6B] truncate max-w-[160px]">
                        {s.owner_email ? (
                          <a href={`mailto:${s.owner_email}`} className="hover:text-ink underline truncate">{s.owner_email}</a>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Link to={`/store/${s.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center text-gold-dark hover:text-ink">
                          <ArrowRight size={14} weight="bold" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </AppShell>
  );
}

function Tile({ label, value, good, bad, subtle }) {
  const tone = bad
    ? "bg-crimson/5 border-crimson/30 text-crimson"
    : good
      ? "bg-emerald-50 border-emerald-200 text-emerald-900"
      : subtle
        ? "bg-cream/40 border-[#E8E0CC] text-[#3a3a3a]"
        : "bg-white border-[#EAEAEA] text-ink";
  return (
    <div className={`rounded-xl border p-3 ${tone}`}>
      <p className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-80">{label}</p>
      <p className="font-spectral text-2xl mt-1 leading-none tracking-tight">{value}</p>
    </div>
  );
}
